<template>
    <div class="comparison">
        <!-- Headers (desktop only) -->
        <div class="comparison__headers--desktop">
            <div class="comparison__header comparison__header--left">
                <MaltImg :src="imageLeftUrl" height="56" :alt="props.leftLabel" />
                <h3 class="comparison__header__label">{{ props.leftLabel }}</h3>
            </div>
            <div class="comparison__header comparison__header--right">
                <MaltImg :src="imageRightUrl" height="56" :alt="props.rightLabel" />
                <h3 class="comparison__header__label">{{ props.rightLabel }}</h3>
            </div>
        </div>

        <!-- Comparison items -->
        <div class="comparison__items">
            <div v-for="(item, index) in items" :key="index" class="comparison__item">
                <div class="comparison__cell comparison__cell--left">
                    <div class="comparison__label--mobile">
                        <MaltImg :src="imageLeftUrl" height="40" :alt="props.leftLabel" />
                        {{ props.leftLabel }}
                    </div>
                    <div class="comparison__content">
                        <VJoyIcon :name="item.leftIsChecked ? 'check' : 'minus'" color="neutral" />
                        <div>{{ item.left }}</div>
                    </div>
                </div>
                <div class="comparison__cell comparison__cell--right">
                    <div class="comparison__label--mobile">
                        <MaltImg :src="imageRightUrl" height="40" :alt="props.rightLabel" />
                        {{ props.rightLabel }}
                    </div>
                    <div class="comparison__content">
                        <VJoyIcon :name="item.rightIsChecked ? 'check' : 'minus'" color="secondary" />
                        <div>{{ item.right }}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {MaltImg} from '#components';
    import {VJoyIcon} from '@maltjoy/core-vue';

    export type ComparisonItem = {
        left: string;
        leftIsChecked: boolean;
        right: string;
        rightIsChecked: boolean;
    };

    const props = defineProps<{
        items: ComparisonItem[];
        leftLabel: string;
        rightLabel: string;
    }>();

    const imageLeftUrl = '/iconstrations/jumping-flash.svg';
    const imageRightUrl = '/iconstrations/hand-crown-fingers.svg';
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    @mixin desktop_mode {
        @include mq.screen_sm(min) {
            @content;
        }
    }

    $border-color-left: var(--joy-color-neutral-30);
    $border-color-right: var(--joy-color-neutral-30);
    $border-width: 1px;
    $border-radius: var(--joy-core-radius-3);
    $desktop-padding: var(--joy-core-spacing-9);
    $mobile-padding: var(--joy-core-spacing-6);

    .comparison {
        width: 100%;

        &__headers--desktop {
            display: none;

            @include desktop_mode {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: var(--joy-core-spacing-9);
                color: var(--joy-color-text-heading);
                text-align: left;
            }
        }

        &__header {
            border-radius: var(--joy-core-radius-3) var(--joy-core-radius-3) 0 0;
            @include text.getFontBySize(secondary-200);

            &--left {
                border: $border-width solid $border-color-left;
            }

            &--right {
                border: $border-width solid $border-color-right;
            }

            &--left,
            &--right {
                border-bottom: none;
                padding: $mobile-padding;

                @include desktop_mode {
                    padding: $desktop-padding $desktop-padding var(--joy-core-spacing-2) $desktop-padding;
                }
            }

            &__label {
                margin-top: var(--joy-core-spacing-3);
            }
        }

        &__items {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-4);

            @include desktop_mode {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 0 var(--joy-core-spacing-9);
                align-items: start;
            }
        }

        &__item {
            display: flex;
            flex-direction: column;
            background-color: var(--joy-color-background-default);
            border: $border-width solid $border-color-left;
            border-radius: $border-radius;

            @include desktop_mode {
                display: contents;
                background: none;
                border-radius: 0;
                border: none;

                &:not(:last-child) {
                    .comparison__cell::after {
                        display: block;
                        clear: both;
                        content: '';
                        position: relative;
                        left: 0;
                        bottom: 0;
                        height: $border-width;
                        width: calc(100% - var(--joy-core-spacing-10));
                        margin: 0 auto;
                    }

                    .comparison__cell--left::after {
                        border-bottom: $border-width solid $border-color-left;
                    }

                    .comparison__cell--right::after {
                        border-bottom: $border-width solid $border-color-right;
                    }
                }

                &:last-child {
                    .comparison__cell--left {
                        border-radius: 0 0 $border-radius $border-radius;
                        border-bottom: $border-width solid $border-color-left;
                    }

                    .comparison__cell--right {
                        border-radius: 0 0 $border-radius $border-radius;
                        border-bottom: $border-width solid $border-color-right;
                    }
                }
            }
        }

        &__cell {
            display: flex;
            flex-direction: column;
            justify-content: flex-start;

            &:last-child {
                padding-bottom: $mobile-padding;
            }

            @include desktop_mode {
                justify-content: center;

                border-radius: 0;
                border-style: solid;
                border-width: 0px $border-width;
                height: 100%;

                box-sizing: content-box;

                &--left {
                    border-color: $border-color-left;
                }

                &--right {
                    border-color: $border-color-right;
                }

                &:last-child {
                    padding-bottom: 0;
                }
            }
        }

        &__label--mobile {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: var(--joy-core-spacing-3);
            @include text.getFontBySize(primary-650);
            padding: $mobile-padding $mobile-padding var(--joy-core-spacing-2) $mobile-padding;
            color: var(--joy-color-text-heading);

            &:last-child {
                padding-top: 0;
            }

            @include desktop_mode {
                display: none;
            }
        }

        &__content {
            text-align: left;
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            gap: var(--joy-core-spacing-2);
            height: 100%;
            padding: 0 $mobile-padding;
            color: var(--joy-color-text-default);

            @include desktop_mode {
                @include text.getFontBySize(primary-400);
                padding: var(--joy-core-spacing-4) $desktop-padding;
            }

            .joy-icon {
                margin-top: var(--joy-core-spacing-1);
            }
        }
    }
</style>
